import { useTranslation } from "react-i18next";
import { X } from "lucide-react";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";

export interface BlogPreviewData {
  title: string;
  excerpt: string;
  contentHtml: string;
  coverImageUrl: string;
  coverImageAlt: string;
  tags: string[];
}

interface BlogPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  data: BlogPreviewData;
}

export function BlogPreviewDialog({
  open,
  onOpenChange,
  data,
}: BlogPreviewDialogProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const today = new Date().toLocaleDateString(
    i18n.language === "en" ? "en-US" : "es-UY",
    { day: "numeric", month: "long", year: "numeric" },
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] gap-0 overflow-hidden p-0 sm:max-w-2xl">
        <div className="sticky top-0 z-20 flex items-center justify-between border-b bg-background px-5 py-3">
          <DialogTitle className="text-sm font-medium text-muted-foreground">
            {t("blog.editor.preview.title")}
          </DialogTitle>
          <DialogClose
            className="rounded-sm p-1 text-muted-foreground transition hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            aria-label={t("blog.editor.preview.close")}
          >
            <X className="h-4 w-4" />
          </DialogClose>
        </div>

        <div className="overflow-y-auto px-6 pb-8 pt-6">
          <article>
            <header className="mb-5 space-y-3">
              {data.tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {data.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
              <h1 className="text-2xl font-semibold leading-tight tracking-tight text-foreground">
                {data.title || t("blog.editor.preview.untitled")}
              </h1>
              {data.excerpt && (
                <p className="text-base leading-relaxed text-muted-foreground">
                  {data.excerpt}
                </p>
              )}
              <time className="block text-xs text-muted-foreground">
                {today}
              </time>
            </header>

            {data.coverImageUrl && (
              <div className="mb-6 overflow-hidden rounded-lg border bg-muted">
                <img
                  src={data.coverImageUrl}
                  alt={data.coverImageAlt || data.title}
                  className="aspect-video w-full object-cover"
                />
              </div>
            )}

            {data.contentHtml ? (
              <div
                className="prose prose-sm max-w-none dark:prose-invert prose-img:rounded-lg prose-headings:tracking-tight"
                dangerouslySetInnerHTML={{ __html: data.contentHtml }}
              />
            ) : (
              <p className="text-sm text-muted-foreground">
                {t("blog.editor.preview.empty")}
              </p>
            )}
          </article>
        </div>
      </DialogContent>
    </Dialog>
  );
}
